package com.siriusxm.pia.views.unifiedaggregator.backfill

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.toCompactDurationString
import com.siriusxm.cmc.utils.toShortDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.BackfillStatus
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.CancellationException
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Instant

val backfillEventPeriods = listOf(
    PeriodType.Days.periodOf(1),
    PeriodType.Days.periodOf(3),
    PeriodType.Days.periodOf(5),
    PeriodType.Weeks.periodOf(1),
    PeriodType.Weeks.periodOf(2)
)

/**
 * Renders the backfill list content (filters and table) without a serviceContentView wrapper.
 * Used inside the BackfillSection tab container.
 */
@Composable
fun backfillsContent(aggregator: AggregatorService) {
    var backfills by remember { mutableStateOf<List<BackfillStatus>?>(null) }
    var region by remember { mutableStateOf<String?>(null) }
    var workload by remember { mutableStateOf<String?>(null) }
    var eventsPeriod by remember {
        mutableStateOf(
            TimeRange(backfillEventPeriods.first())
        )
    }

    var failureMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(eventsPeriod) {
        val range = eventsPeriod.range()
        // The backend rejects some ranges (e.g. one longer than it allows) with a 400. Uncaught,
        // that crashed the page; show the message in place of the table instead, and keep the
        // header so another range can be picked (UCA-1168). An empty list stops the spinner.
        try {
            backfills = aggregator.clientApi.getBackfillJobs(
                range.start,
                range.endInclusive
            ).sortedBy { it.startTime ?: Instant.DISTANT_PAST }.reversed()
            failureMessage = null
        } catch (e: CancellationException) {
            throw e
        } catch (t: Throwable) {
            backfills = emptyList()
            failureMessage = t.message ?: "We were unable to load the backfill jobs."
        }
    }

    if (backfills == null) {
        Div({
            style {
                padding(40.px)
                property("text-align", "center")
            }
        }) {
            spinner(size = Size.LARGE, delay = 750)
        }
    } else {
        box({
            paddedContent = false
            header {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    Select({
                        title("Region")
                        onChange {
                            region = it.value?.takeIf { it != "all" }
                        }
                    }) {
                        Option("all", {
                            if (region == null) {
                                selected()
                            }
                        }) {
                            Text("All Regions")
                        }
                        listOf("us-east-2", "us-east-1", "us-west-2").forEach {
                            Option(it, {
                                if (region == it) selected()
                            }) {
                                Text(it)
                            }
                        }
                    }
                    Select({
                        title("Workload")
                        onChange {
                            workload = it.value?.takeIf { it != "all" }
                        }
                    }) {
                        Option("all", {
                            if (workload == null) selected()
                        }) { Text("All Workloads") }

                        backfills?.mapNotNull { it.workload }?.distinct()?.forEach {
                            Option(it, {
                                if (workload == it) selected()
                            }) {
                                Text(it)
                            }
                        }
                    }
                    Div({ style { flex(1) } })
                    timeRangeSelect(
                        backfillEventPeriods,
                        selected = eventsPeriod
                    ) {
                        eventsPeriod = it ?: TimeRange(backfillEventPeriods.first())
                    }
                }
            }
        }) {
            failureMessage?.let { boxMessage(it) }
            if (failureMessage == null) table<BackfillStatus> {
                items(backfills.orEmpty().filter {
                    region == null || it.region == region
                }.filter {
                    workload == null || it.workload == workload
                })

                onRowClick(hoverHighlight = true) {
                    aggregator.navigate("backfill/${it.jobId}")
                }

                column {
                    width = 40.px
                    content {
                        when (it.status) {
                            "COMPLETE" -> {
                                icon("check_circle") {
                                    style { color(SXMUI.goColor.value()) }
                                }
                            }

                            "FAILED" -> {
                                icon("warning") {
                                    style { color(Color.red) }
                                }
                            }

                            "CANCELLED" -> {
                                icon("cancel") {
                                    style { color(Color.gray) }
                                }
                            }

                            "ACTIVE" -> {
                                icon("timelapse")
                            }
                        }
                    }
                }

                column {
                    title = "Date"
                    width = 12.em
                    content {
                        // Render in the globally-selected timezone (header picker), not the
                        // browser's system zone: project into the chosen IANA zone first.
                        it.startTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString()?.let {
                            Div {
                                Text(it)
                            }
                        }
                    }
                }

                column {
                    title = "Target"
                    width = 24.em
                    content {
                        if (it.targetArn != null) {
                            Div({
                                style {
                                    padding(4.px, 0.px)
                                }
                            }) {
                                Div({
                                    style {
                                        fontSize(0.75.em)
                                        color(SXMUI.subtleTextColor.value())
                                        lineHeight(1.2.cssRem)
                                    }
                                }) {
                                    Text(it.targetType.orEmpty())
                                }
                                Div({
                                    style {
                                        lineHeight(1.3.cssRem)
                                    }
                                }) {
                                    A(href = "#aggregator/backfill/${it.jobId}") {
                                        Text(it.targetName.orEmpty().removePrefix("app-"))
                                    }
                                }
                            }
                        }
                    }
                }

                column {
                    title = "Data Timestamp"
                    content {
                        // Render in the globally-selected timezone (header picker).
                        it.timestamp?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString()?.let {
                            Text(it)
                        }
                    }
                }

                column {
                    title = "Region"
                    content {
                        val targetRegion = it.targetArn?.split(":")?.get(3).orEmpty()
                            .removePrefix("us-")
                        Text(targetRegion)
                    }
                }

                column {
                    title = "Count"
                    content {
                        Text((it.count ?: 0).toLocaleString())
                    }
                }
                column {
                    title = "Duration"
                    content {
                        Text(it.elapsedTime().toCompactDurationString())
                    }
                }
            }
        }

    }
}


val BackfillStatus.targetName: String?
    get() {
        val arnSplits = targetArn?.split(":") ?: return null
        return if (arnSplits[2] == "logs") {
            targetArn!!.substringAfter("log-group:").substringBefore(':')
        } else {
            targetArn!!.substringAfterLast("/")
        }
    }

val BackfillStatus.targetType: String?
    get() {
        val arnSplits = targetArn?.split(":") ?: return null
        return when (arnSplits[2]) {
            "events" -> "Event Bus"
            "logs" -> "Log Group"
            "kinesis" -> "Kinesis Stream"
            "lambda" -> "Lambda"
            "sqs" -> "SQS Queue"
            else -> arnSplits[2]
        }
    }

val BackfillStatus.region: String?
    get() {
        return targetArn?.split(":")?.get(3)
    }


val BackfillStatus.workload: String?
    get() {
        return targetName?.let {
            if (it.startsWith("app-")) {
                it.removePrefix("app-").split("-").let {
                    "${it.first()}-${it.get(1)}"
                }
            } else null
        }
    }


fun BackfillStatus.elapsedTime(): Duration {
    val now = Clock.System.now()
    return (completeTime ?: now) - (startTime ?: now)
}
